import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import {
	transformGlobalContent,
	transformThirdPartyContent,
} from '@confluence/page-card/entry-points/transformers.ts';
import { getThirdPartyItem } from '@confluence/side-navigation/entry-points/recentThirdPartyItems.ts';

import type { HomeRecentActivitiesQuery_activities_Activities_myActivities_MyActivities_all_ActivitiesConnection_edges_ActivityEdge_node_ActivitiesItem as Recent } from './Recent/__types__/HomeRecentActivitiesQuery.ts';
import type {
	HomeRecentActivityQuery_activity_myActivity_all_edges_node as RecentActivityV3,
	HomeRecentActivityQuery_activity_myActivity_all_edges_node_object_data_ConfluencePage as ConfluencePage,
} from './Recent/__types__/HomeRecentActivityQuery.ts';
import type { HomeRecentCreatedQuery_search_ConfluenceSearchConnection_nodes_ConfluenceSearchResponse as Created } from './Recent/__types__/HomeRecentCreatedQuery.ts';
import type { HomeRecentActivityQuery$data } from './Recent/__generated__/HomeRecentActivityQuery.graphql.ts';
import type { useDraftsDataHomeDraftsQuery$data } from './Drafts/__generated__/useDraftsDataHomeDraftsQuery.graphql.ts';
import { ActivityEventType, isCreatorCurrentUser } from './homeViewsQueryHelpers.ts';

type Draft = NonNullable<
	NonNullable<NonNullable<useDraftsDataHomeDraftsQuery$data['search']>['nodes']>[number]
>;

// The Relay type, since the legacy `__types__` one has no 3P (`External*`) data
type ThirdPartyActivity = NonNullable<
	NonNullable<HomeRecentActivityQuery$data['activity']>['myActivity']
>['all']['edges'][number]['node'];

const getLastActionFromRecent = (
	recent: Recent | RecentActivityV3 | ThirdPartyActivity,
	isActivityV3HomeRecent: boolean,
) => {
	const timestamp = isActivityV3HomeRecent
		? (recent as RecentActivityV3).event?.timestamp || ''
		: (recent as Recent).timestamp || '';
	const recentAction = isActivityV3HomeRecent
		? (recent as RecentActivityV3).event?.eventType
		: (recent as Recent).object?.events?.[0].eventType;
	let action: GlobalPageCard['lastAction']['action'];
	switch (recentAction) {
		case ActivityEventType.COMMENTED:
			action = 'commented';
			break;
		case ActivityEventType.EDITED:
		case ActivityEventType.UPDATED:
		case ActivityEventType.PUBLISHED:
		case ActivityEventType.SNAPSHOTTED:
			action = 'edited';
			break;
		case ActivityEventType.CREATED:
		case ActivityEventType.STARTED:
			action = 'createdByYou';
			break;
		case ActivityEventType.VIEWED:
			action = 'visited';
			break;
		case ActivityEventType.LIKED:
			action = 'liked';
			break;
	}
	return {
		when: timestamp,
		action,
	};
};

export const transformRecentToPageCard = (
	recent: Recent | RecentActivityV3,
	untitledDraftText = '',
	isWhiteboardEmojiFGEnabled = false,
	isActivityV3HomeRecent = false,
): GlobalPageCard => {
	const content = isActivityV3HomeRecent
		? (recent as RecentActivityV3).object?.data
		: (recent as Recent).object?.content;

	return {
		...transformGlobalContent(
			content,
			untitledDraftText,
			isWhiteboardEmojiFGEnabled,
			isActivityV3HomeRecent,
		),

		// Using ConfluencePage to fix type error caused by ConfluenceFooterComment not having id in the types file
		key: isActivityV3HomeRecent
			? `${(content as ConfluencePage)?.id}-${(recent as RecentActivityV3)?.event?.eventType}`
			: `${(content as ConfluencePage)?.id}-${(recent as Recent).object?.events?.[0].eventType}`,
		lastAction: getLastActionFromRecent(recent, isActivityV3HomeRecent),
	};
};

/** A third-party (3P) activity, e.g. a Google Drive file. Null when there's nothing to show. */
export const transformThirdPartyToPageCard = (
	recent: ThirdPartyActivity,
): GlobalPageCard | null => {
	const item = getThirdPartyItem(recent.object.data);
	if (!item) {
		return null;
	}

	const lastAction = getLastActionFromRecent(recent, true);
	return {
		...transformThirdPartyContent({ id: recent.object.id, ...item }),
		key: `${recent.object.id}-${recent.event?.eventType}`,
		// Most 3P activity is a click (opening the item, or its smart link), so it reads as a visit
		lastAction: ['CLICKED', 'SL-CLICKED'].includes(recent.event?.eventType)
			? { ...lastAction, action: 'visited' }
			: lastAction,
	};
};

export const transformCreatedByMeToPageCard = (
	created: Created,
	untitledDraftText = '',
	userId: string | null = null,
): GlobalPageCard => {
	const id = created?.content?.id || '';
	const content = created?.content;

	const isContentCreatedByCurrentUser = isCreatorCurrentUser(userId, content);
	const actionText = !isContentCreatedByCurrentUser ? 'ownedByYou' : 'createdByYou';

	return {
		...transformGlobalContent(content, untitledDraftText),
		key: `${id}-created`,
		lastAction: {
			when: created.content?.metadata.createdDate || '',
			action: actionText,
		},
	};
};

export const transformDraftToPageCard = (draft: Draft, untitledDraftText = ''): GlobalPageCard => {
	const content = draft?.content;
	const id = content?.id || '';

	return {
		...transformGlobalContent(content, untitledDraftText),
		key: `${id}-draft`,
		lastAction: {
			when: content?.metadata.lastModifiedDate || '',
			action: 'edited',
		},
	};
};

export const transformStarredToPageCard = (data: any, untitledDraftText = ''): GlobalPageCard => {
	return {
		...transformGlobalContent(data, untitledDraftText),
		lastAction: {
			when: data?.metadata.currentuser?.viewed?.lastSeen || data?.history?.lastUpdated?.when || '',
			action: data?.metadata.currentuser?.viewed?.lastSeen ? 'visited' : 'updated',
		},
	};
};
